import { useCallback, useEffect, useRef } from 'react';

import { INTERCOM_APP_ID } from '../../constants/constants';
import Users from '../../services/api/users/users';

declare global {
  interface Window {
    Intercom: (...args: unknown[]) => void;
    intercomSettings: Record<string, unknown>;
  }
}

function loadIntercomScript(appId: string): void {
  window.intercomSettings = { app_id: appId };

  const existingScript = document.getElementById('intercom-script');

  if (!existingScript) {
    const script = document.createElement('script');
    script.id = 'intercom-script';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    document.head.appendChild(script);
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function () {
    const w = window as any;
    const ic = w.Intercom;
    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const i = function (...args: any[]) {
        i.c(args);
      } as any;
      i.q = [] as any[];
      i.c = function (args: any) {
        i.q.push(args);
      };
      w.Intercom = i;
    }
  })();
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export function showIntercom(): void {
  if (!INTERCOM_APP_ID || typeof window.Intercom !== 'function') return;
  window.Intercom('show');
}

export const useIntercom = () => {
  const isBootedRef = useRef(false);
  const sessionIdRef = useRef(0);

  const bootAnonymous = useCallback(() => {
    if (!INTERCOM_APP_ID) return;

    sessionIdRef.current += 1;
    loadIntercomScript(INTERCOM_APP_ID);
    window.Intercom('boot', {
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: false
    });
    isBootedRef.current = true;
  }, []);

  const boot = useCallback(async () => {
    if (!INTERCOM_APP_ID) return;

    sessionIdRef.current += 1;
    const currentSession = sessionIdRef.current;

    try {
      loadIntercomScript(INTERCOM_APP_ID);
      const token = await Users.getIntercomToken();

      if (sessionIdRef.current !== currentSession) return;

      window.Intercom('boot', {
        app_id: INTERCOM_APP_ID,
        intercom_user_jwt: token,
        hide_default_launcher: true
      });
      isBootedRef.current = true;
    } catch {
      // Silently fail — Intercom is non-critical
    }
  }, []);

  const update = useCallback(async () => {
    if (!INTERCOM_APP_ID || !isBootedRef.current) return;

    const currentSession = sessionIdRef.current;

    try {
      const token = await Users.getIntercomToken();

      if (sessionIdRef.current !== currentSession) return;

      window.Intercom('update', { intercom_user_jwt: token });
    } catch {
      // Silently fail
    }
  }, []);

  const shutdown = useCallback(() => {
    if (!isBootedRef.current) return;
    sessionIdRef.current += 1;
    window.Intercom('shutdown');
    isBootedRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      if (isBootedRef.current) {
        window.Intercom('shutdown');
        isBootedRef.current = false;
      }
    };
  }, []);

  return { boot, bootAnonymous, update, shutdown };
};
